import { Element, NoneElement } from '@thive/canvas';
import { Vector2, Vector3 } from 'three';
import { InterAreaConnection } from '@thive/fddm-types';

import {
  findPolygonOverlap,
  makeOffsetPoly,
  polygonCentroid,
} from '@/modules/common/helpers/polygon';
import { createDistantConnectionElement } from '@/modules/connections/mappers/connection/distantConnection';
import { assertNever } from '@/helpers/utils';
import { useCanvasStore } from '@/modules/canvas/store';
import { Z_INDEX_MAP } from '@/modules/canvas/constants/elements';
import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { createBubbleElement } from '@/modules/connections/mappers/connection/bubble';
import {
  angleBetweenVectors,
  polygonFromAreaOrSegment,
} from '@/modules/floorplanDesign/helpers/helpers';
import { FDDM_TAG, INTERACTIVITY_OFF } from '@/modules/floorplanDesign/constants';
import { InterAreaConnectionUtils } from '@/modules/connections/interAreaConnection';
import {
  findNearestPointOnLine,
  findClosestPointsBetweenPolygons,
} from '@/modules/common/helpers/math';
import { RAD2DEG } from 'three/src/math/MathUtils.js';
import { LineSegment } from '@/modules/common/types/general';
import { logger } from '@common/logging/logger';

export const connectionRotation = (
  areaCenter: Vector2,
  objectA: Vector2[],
  point: Vector2,
): number => {
  const segmentA = findClosestSegmentToPoint(objectA, point);
  if (!segmentA) return 0;

  const vectorA = segmentA.end.clone().sub(segmentA.start).normalize().multiplyScalar(-1);
  const pointOnSegment = findNearestPointOnLine(areaCenter, segmentA);
  const vectorB = areaCenter.clone().sub(pointOnSegment).normalize().multiplyScalar(-1);
  return Math.round(
    angleBetweenVectors(vectorA, new Vector2(1, 0)) * RAD2DEG +
      (angleBetweenVectors(vectorA, vectorB) >= 0 ? 0 : 180),
  );
};

export const createInterAreaConnectionElements = (
  interAreaConnection: InterAreaConnection,
  visible = true,
): Element[] => {
  const canvas = useCanvasStore.getState().instance;
  if (!canvas) return [];

  const { getAreaOrSegmentById } = useFloorplanDesignStore.getState();
  const zoom = canvas.getZoom();
  const { id, idA, idB } = interAreaConnection;

  const areaOrSegmentA = getAreaOrSegmentById(idA);
  if (!areaOrSegmentA) return [];

  const areaOrSegmentB = getAreaOrSegmentById(idB);
  if (!areaOrSegmentB) return [];

  const polygonA = polygonFromAreaOrSegment(areaOrSegmentA);
  if (!polygonA) {
    logger.error(
      'Could not determine a polygon for entity referenced by areaConnection.idA: ',
      areaOrSegmentA,
    );
    return [];
  }

  const polygonB = polygonFromAreaOrSegment(areaOrSegmentB);
  if (!polygonB) {
    logger.error(
      'Could not determine a polygon for entity referenced by areaConnection.idB: ',
      areaOrSegmentB,
    );
    return [];
  }

  let renderType: 'crossing' | 'distCon' | 'regCon' = 'regCon';

  const overlapCenter = InterAreaConnectionUtils.findOverlapCenter(polygonA, polygonB);

  if (!overlapCenter) {
    renderType = 'distCon';
  } else {
    const isConnectionBetweenHighways =
      areaOrSegmentA.type === 'segment' && areaOrSegmentB.type === 'segment';

    if (
      isConnectionBetweenHighways &&
      InterAreaConnectionUtils.isCrossingConnection(interAreaConnection)
    ) {
      renderType = 'crossing';
    } else {
      renderType = 'regCon';
    }
  }

  switch (renderType) {
    case 'crossing': {
      const overlapPolygon = findPolygonOverlap(
        makeOffsetPoly(polygonA, InterAreaConnectionUtils.collision_tolerance_in_meter),
        makeOffsetPoly(polygonB, InterAreaConnectionUtils.collision_tolerance_in_meter),
      );

      if (!overlapPolygon) {
        logger.error(
          'Could unexpectedly not find a connection point between polygons based on 2 following areas. Should have been removed / cleaned up?',
          areaOrSegmentA,
          areaOrSegmentB,
        );

        return [];
      }

      return buildCrossingConnectionElement(
        id,
        polygonCentroid(overlapPolygon).multiplyScalar(1000),
        zoom,
        interAreaConnection,
        visible,
      );
    }
    case 'distCon': {
      const { pointOnA, pointOnB } = findClosestPointsBetweenPolygons(polygonA, polygonB);
      return createDistantConnectionElement(
        id,
        pointOnA.clone().multiplyScalar(1000),
        pointOnB.clone().multiplyScalar(1000),
        zoom,
        interAreaConnection,
        visible,
      );
    }
    case 'regCon': {
      if (!overlapCenter) {
        logger.error(
          'Could unexpectedly not find a connection point between polygons based on 2 following areas. Should have been removed / cleaned up?',
          areaOrSegmentA,
          areaOrSegmentB,
        );

        return [];
      }

      const rot = connectionRotation(
        polygonCentroid(polygonA),
        polygonA,
        new Vector2(overlapCenter.x, overlapCenter.y),
      );

      return createRegularConnectionElement(
        id,
        new Vector2(overlapCenter.x, overlapCenter.y).multiplyScalar(1000),
        rot,
        zoom,
        interAreaConnection,
        visible,
      );
    }
    default:
      assertNever(renderType);
  }
};

export const interAreaConnectionMapper = (
  interAreaConnections: InterAreaConnection[],
  visible = true,
): Element[] =>
  interAreaConnections.flatMap((interAreaConnection) =>
    createInterAreaConnectionElements(interAreaConnection, visible),
  );

//
// Utility methods
//

const distanceToSegmentSquared = (point: Vector2, segment: LineSegment): number => {
  const segmentVector = segment.end.clone().sub(segment.start);
  const segmentLengthSquared = segmentVector.lengthSq();

  if (segmentLengthSquared === 0) {
    return point.distanceToSquared(segment.start);
  }

  const pointVector = point.clone().sub(segment.start);
  const projection = pointVector.dot(segmentVector) / segmentLengthSquared;
  const clampedProjection = Math.max(0, Math.min(1, projection));
  const nearestPoint = segment.start.clone().add(segmentVector.multiplyScalar(clampedProjection));

  return point.distanceToSquared(nearestPoint);
};

const findClosestSegmentToPoint = (object: Vector2[], point: Vector2): LineSegment | undefined => {
  if (object.length < 2) return undefined;

  let minDistance = Infinity;
  let closestSegment: LineSegment | undefined;

  for (let index = 0; index < object.length; index++) {
    const nextIndex = (index + 1) % object.length;
    const segment = { start: object[index], end: object[nextIndex] };
    if (segment.start.distanceToSquared(segment.end) === 0) continue;

    const distance = distanceToSegmentSquared(point, segment);

    if (distance < minDistance) {
      minDistance = distance;
      closestSegment = segment;
    }
  }

  return closestSegment;
};

const createConnectionGroup = (id: string, center: Vector2, angle: number): NoneElement => ({
  id,
  type: 'None',
  name: id,
  position: new Vector3(center.x, center.y, Z_INDEX_MAP[FDDM_TAG.BUBBLE]),
  rotation: new Vector3(0, 0, angle),
  interactivity: INTERACTIVITY_OFF,
  tags: [FDDM_TAG.LAYER_CONNECTIONS],
});

const createConnectionBubbleElements = (
  id: string,
  center: Vector2,
  angle: number,
  zoom: number,
  interAreaConnection: InterAreaConnection,
  visible: boolean,
): Element[] => {
  const group = createConnectionGroup(id, center, angle);

  return [group, ...createBubbleElement(id, group.id, zoom, interAreaConnection, [], visible)];
};

const buildCrossingConnectionElement = (
  id: string,
  position: Vector2,
  zoom: number,
  interAreaConnection: InterAreaConnection,
  visible = true,
): Element[] => createConnectionBubbleElements(id, position, 0, zoom, interAreaConnection, visible);

const createRegularConnectionElement = (
  id: string,
  center: Vector2,
  angle: number,
  zoom: number,
  interAreaConnection: InterAreaConnection,
  visible = true,
): Element[] =>
  createConnectionBubbleElements(id, center, angle, zoom, interAreaConnection, visible);
